import { useMutation } from "@apollo/client";
import { Box, Paper, Stack, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DOCUMENT_WITH_METADATA } from "components/Document/ReviewDocumentMetadataDialog/queries";
import { gql } from "generated-graphql";
import { EventMutationScope, UpdateTaskCoreInput } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useCallback } from "react";
import { Controller, FormProvider, Resolver, useForm, useFormState } from "react-hook-form";

import { DocumentsListControl } from "./DocumentsControl";
import { LinksControl } from "./LinksControl";
import SubtasksControl from "./SubtasksControl";
import { TaskDialogSkeleton } from "./TaskDialogSkeleton";
import { TaskPickerToolbar } from "./TaskPickerToolbar";
import { TaskTitleHeader } from "./TaskTitleHeader";
import { TextInput } from "./TextInput";
import { TaskFormType } from "./types";
import useDeleteTaskMutation from "./useDeleteTask";

const UPDATE_TASK = gql(`
  mutation UpdateTask($input: UpdateTaskCoreInput!) {
    updateTaskCore(input: $input) {
      id
    }
  }
`);

export interface TaskFormProps {
  initialValues: TaskFormType;
  isLoading: boolean;
  validationResolver?: Resolver<TaskFormType>;

  // UI configuration
  /** Show view in tasks button */
  showViewInTasks?: boolean;
  /** Virtual event id used for occurrence-level comments */
  virtualEventId?: string;
}

/**
 * A form for editing a task that autosaves changes to the task (not virtual event).
 */
export const AutosaveEditTaskForm: React.FC<TaskFormProps> = ({
  initialValues,
  isLoading,
  validationResolver,
  showViewInTasks = false,
  virtualEventId,
}) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const form = useForm<TaskFormType>({
    defaultValues: initialValues,
    resolver: validationResolver,
    mode: "onChange",
  });
  const { control, watch } = form;
  const { isDirty, isSubmitting } = useFormState({ control });

  const completedOn = watch("completedOn");
  const completionTiming = watch("completionTiming");
  const completionDeltaDays = watch("completionDeltaDays");
  const taskStatus = watch("taskStatus");
  const isSuggested = watch("isSuggested");

  const [updateTask] = useMutation(UPDATE_TASK);

  const onSubmit = useCallback(
    async (data: TaskFormType) => {
      const input: UpdateTaskCoreInput = {
        tenantId: data.tenantId ?? "",
        taskId: data.taskId ?? "",
        title: data.title,
        description: data.description,
        startDate: data.deadline ?? undefined,
        facilityIds: data.facilityIds,
        assigneeId: data.assignee?.id ?? undefined,
        watcherIds: data.watchers.map((watcher) => watcher.id),
        documentIds: data.documents.map((document) => document.id),
        reminderDays: data.reminderDays.map((reminder) => reminder.days),
        subtasks: data.subtasks.map((subtask) => ({
          id: subtask.id,
          title: subtask.title,
        })),
        links: data.links.map((link) => link.url),
        frequency: data.frequency,
        interval: data.interval,
        tags: data.tags,
      };

      if (!data.taskId || !data.tenantId || !isDirty) {
        return;
      }
      await updateTask({ variables: { input } });
    },
    [updateTask, isDirty]
  );

  const handleFormBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const related = event.relatedTarget;
      if (related instanceof Node && event.currentTarget.contains(related)) {
        return;
      }
      if (!isDirty || isSubmitting) {
        return;
      }
      void form.handleSubmit(onSubmit)();
    },
    [form, onSubmit, isDirty, isSubmitting]
  );

  const [deleteTask] = useDeleteTaskMutation();
  const onDeleteTask = useCallback(
    (scope: EventMutationScope) => {
      deleteTask({
        variables: {
          input: {
            taskId: initialValues.taskId ?? "",
            date: initialValues.deadline ?? undefined,
            eventMutationScope: scope,
          },
        },
        refetchQueries: [DOCUMENT_WITH_METADATA],
      });
    },
    [deleteTask, initialValues.taskId, initialValues.deadline]
  );

  const [deleteDocument] = useDeleteDocumentMutation({
    onError: () => {
      alerts.error("Failed to delete document");
    },
  });

  return (
    <Paper
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.grey[300]}` }}
      onBlur={handleFormBlur}
    >
      <FormProvider {...form}>
        {isLoading ? (
          <TaskDialogSkeleton />
        ) : (
          <Stack my={2} mx={3} gap={theme.spacing(2.5)} sx={{ pb: 2 }}>
            <TaskTitleHeader
              control={control}
              initialValues={initialValues}
              completedOn={completedOn}
              completionTiming={completionTiming}
              completionDeltaDays={completionDeltaDays}
              taskStatus={taskStatus}
              isSuggested={isSuggested}
              onDeleteTask={() => onDeleteTask(EventMutationScope.All)}
              showViewInTasks={showViewInTasks}
              virtualEventId={virtualEventId}
              readonlyStatusPicker
            />

            <Stack gap={theme.spacing(2)}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextInput
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    variant="description"
                    placeholder="Add task description..."
                    multiline={true}
                    maxRows={10}
                  />
                )}
              />

              <SubtasksControl control={control} />
              <DocumentsListControl
                control={control}
                onDelete={(id: string) => deleteDocument({ variables: { id } })}
              />
              <LinksControl control={control} />
            </Stack>

            <Box
              sx={{
                px: 1.25,
                py: 1.25,
                borderRadius: 2,
                bgcolor: "grey.50",
                border: (theme) => `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              <Stack direction="row" gap={theme.spacing(1)} width="100%" flexWrap="wrap">
                <TaskPickerToolbar form={form} showActionButtons={false} />
              </Stack>
            </Box>
          </Stack>
        )}
      </FormProvider>
    </Paper>
  );
};
